import React, { type FC } from 'react'
import LanguageStrings from '../../../../i18n/locales'
import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
  TableBody
} from '@mui/material'
import { useSelector } from 'react-redux'
import EmptyTable from './EmptyTable'
import AddMoreDetails from '../configureDevice'
import { GenXDropdown } from '../../../../components/GenXDropdown'
import { type RootState } from '../../../../store'
import { type FrequencyOption } from '../../services/frequencyMetaDataService'

const AddDeviceStrings = LanguageStrings().DeviceManagementCollectionStrings.AddDeviceComponent

// Default fallback options (for backward compatibility)
const defaultExecutionFrequencyOptions = [
  { value: '1', label: '1 Minute (Default)' },
  { value: '3', label: '3 Minutes' },
  { value: '5', label: '5 Minutes' }
]

// Helper function to convert minutes to display value for dropdown
const convertMinutesToDisplayValueOption = (minutes: number | null, isTotalizer = false, options: FrequencyOption[]): { value: string, label: string } => {
  if (isTotalizer) {
    const defaultOption = options.find(opt => opt.label.includes('Default') || opt.label.includes('1'))
    return defaultOption !== undefined ? { value: defaultOption.value, label: defaultOption.label } : { value: '1', label: '1 Minute' }
  }

  if (minutes === null || minutes === undefined) {
    const defaultOption = options.find(opt => opt.label.includes('Default') || opt.label.includes('1'))
    return defaultOption !== undefined ? { value: defaultOption.value, label: defaultOption.label } : { value: '1', label: '1 Minute' }
  }

  const option = options.find(opt => parseInt(opt.value) === minutes)
  if (option !== undefined) {
    return { value: option.value, label: option.label }
  }

  // Fallback to default
  const defaultOption = options.find(opt => opt.label.includes('Default') || opt.label.includes('1'))
  return defaultOption !== undefined ? { value: defaultOption.value, label: defaultOption.label } : { value: '1', label: '1 Minute' }
}

// Helper function to convert display value to minutes
const convertDisplayValueToMinutes = (displayValue: { value: string, label: string }): number => {
  return parseInt(displayValue.value)
}

const styles = {
  selectAllCheckbox: {
    color: '#909090'
  },
  checkbox: {
    color: '#909090'
  },
  table: {
    backgroundColor: '#272727',
    borderBottom: 'none',
    boxShadow: 'none !important'
  },
  tableHead: {
    backgroundColor: '#303030',
    border: '1px solid #101010',
    boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.15)',
    borderRadius: '10px 10px 0px 0px'
  },
  tableHeadCell: {
    backgroundColor: '#303030',
    borderBottom: 'none',
    padding: '5px'
  },
  tableCell: {
    backgroundColor: '#272727',
    borderBottom: 'none',
    padding: '5px'
  },
  tableHeaderTypography: {
    color: '#F0F0F0',
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: 'Honeywell Sans Web',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 800,
    lineHeight: '24px'
  },
  tableCellTypography: {
    color: '#F0F0F0',
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: 'Honeywell Sans Web',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '24px'
  },
  tableContainer: {
    height: '375px',
    maxHeight: '375px',
    overflow: 'auto'
  }
}

interface Indicator {
  id: string
  kpiDefinitionId: string
  kpiName: string
  isKpiEnabled: boolean
  isAlarmApplicable: boolean
  isAlarmEnabled: boolean
  alarmLowerLimit: number | null
  alarmHigherLimit: number | null
  alarmHigherLimitSeverity: string | null
  alarmLowerLimitSeverity: string | null
  isCustomKpi: boolean
  executionFrequencyMinutes: number
  unit: string
  alarmHigherLimitSeverityId: string | null
  alarmLowerLimitSeverityId: string | null
  isTotalizer?: boolean
}

interface PerformanceIndicatorsProps {
  tableHeaders: string[]
  indicators: Indicator[]
  instanceType: 'standard' | 'custom'
  selectedCount: number
  toggleSelectAll: () => void
  toggleSelectIndicator: (id: string) => void
  handleAlarmChange: (id: string) => void
  handleChangeTextBox: (
    id: string | number,
    propertyName: string,
    value: string | number
  ) => void
  handleAlarmSelect: (id: string | number, propertyName: string, value: string) => void
  handleCancel: (id: string, isCustomKpiFlag: boolean) => void
}

const PerformanceIndicatorsTable: FC<PerformanceIndicatorsProps> = ({
  tableHeaders,
  indicators,
  instanceType,
  selectedCount,
  handleAlarmSelect,
  toggleSelectAll,
  toggleSelectIndicator,
  handleAlarmChange,
  handleChangeTextBox,
  handleCancel
}) => {
  // Get frequency options from Redux store
  const frequencyMetaDataState = useSelector((state: RootState) => state.frequencyMetaDataState)
  const executionFrequencyOptions = frequencyMetaDataState.data?.kpiFrequencies ?? defaultExecutionFrequencyOptions

  const getTestIdPrefix = (): string => {
    return instanceType === AddDeviceStrings.standard
      ? 'std'
      : AddDeviceStrings.custom
  }
  return (
    <TableContainer className="indicator-table" sx={styles.tableContainer}>
      <Table sx={styles.table} stickyHeader>
        <TableHead sx={styles.tableHead}>
          <TableRow sx={{ zIndex: 3, position: 'sticky' }}>
            <TableCell sx={styles.tableHeadCell}>
              <Checkbox
                sx={styles.selectAllCheckbox}
                data-testid={`${getTestIdPrefix()}-select-all-checkbox`}
                onChange={toggleSelectAll}
                indeterminate={
                  selectedCount > 0 && selectedCount < indicators.length
                }
                checked={
                  indicators.length > 0 && selectedCount === indicators.length
                }
                inputProps={{
                  'aria-label': 'Select all'
                }}
              />
            </TableCell>
            {tableHeaders.map((header) => (
              <TableCell key={header} sx={styles.tableHeadCell}>
                <Typography sx={styles.tableHeaderTypography}>
                  {header}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {indicators.length === 0
          ? (
            <EmptyTable />
            )
          : (
            <TableBody className="indicator-table-body">
              {indicators.map((indicator, index) => (
                <TableRow key={indicator.kpiDefinitionId}>
                  <TableCell sx={styles.tableCell}>
                    <Checkbox
                      color="primary"
                      data-testid={`${getTestIdPrefix()}-select-indicator-${
                        indicator.kpiDefinitionId
                      }`}
                      sx={styles.checkbox}
                      checked={indicator.isKpiEnabled}
                      onChange={() => {
                        toggleSelectIndicator(indicator.kpiDefinitionId)
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...styles.tableCell, ...{ minWidth: '250px', width: { sm: '30%', md: '30%', lg: '350px' } } }}>
                    <Typography sx={styles.tableCellTypography}>
                      {indicator.kpiName}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...styles.tableCell, minWidth: '195px', padding: 1.2 }}>
                        <GenXDropdown
                          menuPlacement={(index > indicators.length - 3 && indicators.length > 2) ? 'top' : 'auto'}
                          value={convertMinutesToDisplayValueOption(indicator.executionFrequencyMinutes, indicator.isTotalizer, executionFrequencyOptions)}
                          onSelect={(selectedOption: { value: string, label: string }) => {
                            const minutes = convertDisplayValueToMinutes(selectedOption)
                            handleChangeTextBox(indicator.kpiDefinitionId, 'executionFrequencyMinutes', minutes)
                          }}
                          options={executionFrequencyOptions}
                          isSearchable={false}
                          height={150}
                          width={198}
                          disabled={!indicator.isKpiEnabled || Boolean(indicator.isTotalizer) || frequencyMetaDataState.isLoading}
                        />
                  </TableCell>
                  <TableCell sx={{ ...styles.tableCell, minWidth: '160px' }}>
                    <AddMoreDetails
                      isEdit={true}
                      isAlarmEnabledCompo={!indicator.isKpiEnabled}
                      deviceIndicators={indicator}
                      handleChangeTextBox={handleChangeTextBox}
                      alarmSelect={handleAlarmSelect}
                      getTestIdPrefix={getTestIdPrefix}
                      handleCancel={handleCancel}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            )}
      </Table>
    </TableContainer>
  )
}

export default PerformanceIndicatorsTable
